import {
  ClientRatePlanData,
  IClientRatePlanApiCall,
  IRestApiService,
  RatePlan,
  RatePlanForCourse,
  RatePlanHistory,
  RatePlansAggregation,
  RatePlanValidation,
  ReportData,
  ReportFilters,
} from '../types';
import moment from 'moment/moment';

class ClientRatePlanApiCall implements IClientRatePlanApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  getRatePlansOfClient(ownerId: string, studioClientId: string, withNumbers: boolean): Promise<RatePlan[]> {
    const url = `client-rate-plan/${ownerId}/${studioClientId}?withNumbers=${withNumbers}`;
    return this.restApiService.get(url);
  }

  getRatePlanOfClient(ownerId: string, studioClientId: string, id: string): Promise<RatePlan> {
    const url = `client-rate-plan/${ownerId}/${studioClientId}/${id}`;
    return this.restApiService.get(url);
  }

  getAllAttendanceOnRatePlan(ownerId: string, clientId: string, clientRatePlanId: string): Promise<any[]> {
    const url = `client-rate-plan/${ownerId}/${clientId}/all-by-rate-plan/${clientRatePlanId}`;
    return this.restApiService.get(url);
  }

  validateRatePlanIntersection(
    ownerId: string,
    studioClientId: string,
    ratePlanId: string,
    from: any,
    to: any,
    permanent: boolean,
    excludeRatePlanId?: string
  ): Promise<RatePlanValidation> {
    let url = `client-rate-plan/${ownerId}/${studioClientId}/check-dates/${ratePlanId}?from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}&permanent=${permanent}`;

    if (excludeRatePlanId) {
      url += `&excludeRatePlanId=${excludeRatePlanId}`;
    }

    return this.restApiService.get(url);
  }

  getRatePlansAgg(ownerId: string, from: any, to: any): Promise<RatePlansAggregation> {
    const url = `client-rate-plan/${ownerId}/rate-plans-agg?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}`;

    return this.restApiService.get(url);
  }

  getRatePlansHistoryOfClient(ownerId: string, studioClientId: string, from: any, to: any): Promise<RatePlanHistory[]> {
    const url = `client-rate-plan/${ownerId}/${studioClientId}/history?from=${moment(from).format(
      'DD-MM-YYYY'
    )}&to=${moment(to).format('DD-MM-YYYY')}`;

    return this.restApiService.get(url);
  }

  getReport(ownerId: string, from: any, to: any, clientsFilter?: ReportFilters): Promise<ReportData> {
    const url = `client-rate-plan/${ownerId}/report?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}&almostDone=${!!clientsFilter?.almostDone}`;

    return this.restApiService.post(url, clientsFilter);
  }

  // Background version of getReport above - same date filter, but ownerId in the URL is now only
  // used to build the path (the server always scopes the export to req.user._id, matching the fix
  // to the sync endpoint's anonymous-access bug in client-rate-plan.route.js).
  startRatePlanReportExport(
    ownerId: string,
    from: any,
    to: any,
    clientsFilter?: ReportFilters
  ): Promise<{ jobId: string; downloadUrl: string }> {
    const url = `client-rate-plan/${ownerId}/report/export-async?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}`;

    return this.restApiService.post(url, clientsFilter);
  }

  addRatePlan(ownerId: string, studioClientId: string, data: ClientRatePlanData): Promise<RatePlan> {
    return this.restApiService.post(`client-rate-plan/${ownerId}/${studioClientId}`, data);
  }

  updateRatePlan(ownerId: string, studioClientId: string, id: string, data: ClientRatePlanData): Promise<RatePlan> {
    return this.restApiService.put(`client-rate-plan/${ownerId}/${studioClientId}/${id}`, data);
  }

  updateRatePlans(ownerId: string, ratePlans: RatePlan[]): Promise<RatePlan[]> {
    return this.restApiService.put(`client-rate-plan/${ownerId}/update-rate-plans`, ratePlans);
  }

  deleteRatePlan(ownerId: string, id: string): Promise<void> {
    return this.restApiService.delete(`client-rate-plan/${ownerId}/${id}`);
  }

  detachAttendanceFromRatePlan(ownerId: string, id: string): Promise<void> {
    return this.restApiService.delete(`client-rate-plan/${ownerId}/attendance/${id}`);
  }

  attachAttendanceFromRatePlan(
    ownerId: string,
    clientId: string,
    attendanceId: string,
    clientRatePlanId: string
  ): Promise<void> {
    return this.restApiService.post(
      `client-rate-plan/${ownerId}/attach-to-rate-plan/${clientId}/${attendanceId}/${clientRatePlanId}`
    );
  }

  getCoursesOnRatePlan(
    ownerId: string,
    studioClientId: string,
    studioId: string,
    billingTag: string,
    from: string,
    to: string
  ): Promise<RatePlanForCourse> {
    return this.restApiService.get(
      `client-rate-plan/${ownerId}/${studioClientId}/get-rate-plan-for-course?studioId=${studioId}&billingTag=${billingTag}&from=${from}&to=${to}`
    );
  }

  getRatePlansForCourse(
    ownerId: string,
    studioClientIds: string[],
    studioId: string,
    billingTag: string,
    from: string,
    to: string
  ): Promise<RatePlanForCourse[]> {
    return this.restApiService.post(
      `client-rate-plan/${ownerId}/get-rate-plan-for-course?studioId=${studioId}&billingTag=${billingTag}&from=${from}&to=${to}`,
      { studioClientIds }
    );
  }
}

export default ClientRatePlanApiCall;
